import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, map, switchMap } from 'rxjs';
import { LanguageService, LpxNavbarItem } from '@volo/ngx-lepton-x.core';
import { ThemeService } from '@volosoft/ngx-lepton-x';
import {
  DefaultSelectedLanguageLabelFn,
  Setting,
  ISettingsService,
  combineAndFilterByChildren,
} from '@volosoft/ngx-lepton-x/layouts';
import { UiColorThemeService } from './ui-color-theme.service';

const COLOR_THEME_GROUP_ID = 'acx-color-theme';

interface SettingsGroup extends Setting {
  children: LpxNavbarItem[];
}

/**
 * Supplies the contents of the General Settings panel.
 *
 * Replacing LeptonX's SidebarSettingsService through LPX_SETTINGS_SERVICE is the theme's own extension
 * point, and it is what removes Container Width: the group is simply never contributed, so neither the
 * panel section nor its toolbar icon is rendered. Nothing is hidden with CSS and the package is untouched.
 */
@Injectable()
export class AcxSidebarSettingsService implements ISettingsService {
  private readonly themeService = inject(ThemeService);
  private readonly languageService = inject(LanguageService);
  private readonly colorThemeService = inject(UiColorThemeService);

  private readonly colorThemeGroup$: Observable<SettingsGroup> =
    this.colorThemeService.selected$.pipe(
    map(selected => ({
      text: 'AgenticCX::Theme',
      icon: 'bi bi-palette',
      id: COLOR_THEME_GROUP_ID,
      children: this.colorThemeService.presets.map(preset => ({
        text: preset.localizationKey,
        icon: preset.iconClass,
        selected: preset.theme === selected,
        action: () => {
          this.colorThemeService.select(preset.theme);
          return true;
        },
      })),
    })),
  );

  readonly settings$: Observable<Setting[]> = combineAndFilterByChildren<SettingsGroup>([
    this.themeService.stylesAsSettingsGroup$,
    this.colorThemeGroup$,
    this.languageService.languagesAsSettingsGroup$,
  ]);

  readonly selectedSettings$: Observable<Setting[]> = this.languageService.languages$.pipe(
    switchMap(languages => {
      const appearance$ = this.themeService.selectedStyle$.pipe(
        map(style => ({ icon: style.icon, id: this.themeService.id })),
      );

      if (languages.length <= 1) {
        return appearance$.pipe(map(appearance => [appearance]));
      }

      const language$ = this.languageService.selectedLanguage$.pipe(
        map(language => DefaultSelectedLanguageLabelFn(language, this.languageService.id)),
      );

      return combineLatest([appearance$, language$]).pipe(
        map(([appearance, language]) => [appearance, language]),
      );
    }),
  );
}
